import React, { useState, useEffect } from 'react';
import {
  Container,
  Col,
  Row,
  Table,
  Button,
  TabContent,
  TabPane,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import { Link, useHistory } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';
import LinkPlusIcon from 'mdi-react/LinkPlusIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import { Parser } from 'json2csv';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import Loading from '../../shared/components/custom/Loading';
import useModal from '../../shared/hooks/useModal';

import Api from '../../util/api';
import t, { partial } from '../../util/translation/translation';
import { getBaseDateTime } from '../../util/date-and-time';
import NavTab from '../../shared/components/custom/NavTab';
import EventCategoryCard from './components/EventCategoryCard/EventCategoryCard';
import FormCard from '../../shared/components/custom/FormCard';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';
import LazyTabPane from '../../shared/components/custom/LazyTabPane';
import ParticipantStatsPage from './ParticipantStatsPage';

// Quick visual read on how full an event is: plenty of room vs filling up
// vs essentially full.
const getCapacityColor = (percent) => {
  if (percent >= 90) return '#f94848';
  if (percent >= 70) return '#f0ad4e';
  return '#4ce1b6';
};

const EventsPage = () => {
  const history = useHistory();
  const [events, setEvents] = useState({ previous: [], upcoming: [] });
  const [handleModal, visible, deleteID] = useModal();
  const [tab, setTab] = useState('1');
  const [eventsView, setEventsView] = useState('upcoming');
  const [eventCategories, setEventCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const p = partial('EventsPage');
  const s = partial('shared');

  const segregateEvents = (incomingEvents) => {
    const previous = incomingEvents.data
      .filter((event) => moment(event.startDate).isSameOrBefore(moment()))
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    const upcoming = incomingEvents.data
      .filter((event) => moment(event.startDate).isAfter(moment()))
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    setEvents({ previous, upcoming });
  };

  const getEventCategories = async () => {
    setLoadingCategories(true);
    try {
      const { data: resCategories } = await Api.eventCategories.all();
      setEventCategories(resCategories);
      setLoadingCategories(false);
    } catch (err) {
      setLoadingCategories(false);
      toast.error(err || p('eventCategoriesFetchError'));
    }
  };

  const getEvents = async () => {
    setLoading(true);
    try {
      const resEvents = await Api.events.all();
      segregateEvents(resEvents);
      await getEventCategories();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err || p('eventsFetchError'));
    }
  };

  const exportEventCategoriesForAllCompanies = async () => {
    setLoading(true);
    try {
      const [resCompanyList, eventCategoriesForCompany] = await Promise.all([
        Api.companies.getCompaniesForClub(),
        Api.eventCategories.all(),
      ]);
      const categoryAndComapnyFlatArray = eventCategoriesForCompany.data.map(
        (category) => {
          return category.companyConfigs.map((config) => ({
            companyId: config.companyId,
            maxAttendes: config.maxAttendes,
            categoryTitle: category.title,
          }));
        }
      );

      const allCompanyName = resCompanyList.reduce((acc, company) => {
        acc[company.id] = { companyName: company.name };

        return acc;
      }, {});

      const finalArray = [];

      const companyKeys = Object.keys(allCompanyName);
      companyKeys.forEach((key) => {
        categoryAndComapnyFlatArray.forEach((a) => {
          const y = a.find((z) => z.companyId === key);
          if (y) {
            finalArray.push({
              ...y,
              ...allCompanyName[key],
            });
          }
        });
      });

      const sortedFinalArray = finalArray.sort((a, b) => {
        return a.companyName.trim() < b.companyName.trim() ? -1 : 1;
      });
      const fields = [
        { label: `${s('companyName')}`, value: 'companyName' },
        { label: `${s('category')}`, value: 'categoryTitle' },
        {
          label: `${t('EventsForm.maxNumberOfParticipant')}`,
          value: 'maxAttendes',
        },
      ];
      const json2csvParser = new Parser({ fields });
      const csv = json2csvParser.parse(sortedFinalArray);
      fileDownload(
        `\uFEFF${csv}`,
        `${t('EventCategories.companyCategoryConfigs')}.csv`
      );

      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err || `${p('listExportingERROR')}'!'`);
    }
  };

  useEffect(() => {
    getEvents();
  }, []);

  const deleteEvent = async () => {
    setLoading(true);
    try {
      await Api.events.delete(deleteID);
      toast.success(p('eventHasBeenDeleted'));
      handleModal();
      getEvents();
    } catch (err) {
      toast.error(err || p('eventDeleteError'));
      getEvents();
    }
  };

  const totalParticipantCount = (participants) =>
    participants.reduce((tot, curr) => {
      return tot + curr.friendAmount;
    }, 0);

  const StatusBadge = (event) => {
    const now = new Date().getTime();
    const eventDate = new Date(event.startDate).getTime();
    const eventRegistrationStart = new Date(event.registrationStart).getTime();
    const eventRegistrationEnd = new Date(event.registrationEnd).getTime();

    switch (true) {
      case now < eventRegistrationStart:
        return <Badge color="primary">{p('registrationNotStarted')}</Badge>;
      case now > eventRegistrationStart && now < eventRegistrationEnd:
        return <Badge color="success">{p('registrationActive')}</Badge>;
      /* case now > eventRegistrationEnd && event.waitlistEnabled:
        return (
          <Badge color="warning">
            {p('waitlist')}
          </Badge>
        ); */
      case now > eventRegistrationEnd && now < eventDate:
        return <Badge color="danger">{p('registrationClosed')}</Badge>;
      case now > eventDate:
        return <Badge color="secondary">{p('eventFinished')}</Badge>;
      default:
        return <div />;
    }
  };

  const renderEvents = (specificEvents) => {
    return specificEvents.map((event) => {
      return (
        <tr key={`event-${event.id}`}>
          <td>
            <Link to={`/events/edit/${event.id}`}>{event.title}</Link>
          </td>
          <td>{s(event.targetAudience)}</td>
          <td>
            <div>{getBaseDateTime(event.startDate)}</div>
            <small className="text-muted d-block mt-1">
              {p('registration')}
            </small>
            <small className="text-muted">
              {s('opens')}:{' '}
              {event.registrationStart
                ? getBaseDateTime(event.registrationStart)
                : '-'}
              <br />
              {s('closes')}:{' '}
              {event.registrationEnd
                ? getBaseDateTime(event.registrationEnd)
                : '-'}
            </small>
          </td>
          <td>
            <div>
              {(() => {
                const signedUpCount =
                  totalParticipantCount(event.participants) +
                  event.participants.length;
                const maxAttendees = event?.maxAttendees;
                const fillPercent = maxAttendees
                  ? Math.min(100, (signedUpCount / maxAttendees) * 100)
                  : 0;
                return (
                  <>
                    <div>
                      {signedUpCount} / {maxAttendees}
                      {event.waitlistEnabled && (
                        <Badge className="m-1 p-1" pill color="warning">
                          <Link
                            style={{ color: '#ED9E45' }}
                            to={`/events/edit/${event.id}?tab=5`}
                          >{` + ${totalParticipantCount(event?.waitingParticipants) + event?.waitingParticipants?.length}`}</Link>
                        </Badge>
                      )}
                    </div>
                    {!!maxAttendees && (
                      <div
                        style={{
                          height: 4,
                          borderRadius: 2,
                          background: '#eee',
                          marginTop: 4,
                          marginBottom: 6,
                          width: 100,
                        }}
                      >
                        <div
                          style={{
                            height: 4,
                            borderRadius: 2,
                            width: `${fillPercent}%`,
                            background: getCapacityColor(fillPercent),
                          }}
                        />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div>
              {StatusBadge(event)}
              {event.waitlistEnabled && (
                <Badge className="m-1" color="warning">
                  {p('waitlist')}
                </Badge>
              )}
            </div>
          </td>
          <td>
            <div className="icon-actions">
              <SquareIconButton
                icon={<LinkPlusIcon />}
                onClick={() => {
                  toast.success(s('linkCopied'));
                  navigator.clipboard.writeText(event.dynamicLink);
                }}
                title={s('copyLink')}
                id={`copy-link-${event.id}`}
              />
              <SquareIconButton
                icon={<EditOutlineIcon />}
                onClick={() => history.push(`/events/edit/${event.id}`)}
                title={s('edit')}
                id={`edit-${event.id}`}
              />
              <SquareIconButton
                icon={<ContentCopyIcon />}
                onClick={() =>
                  history.push({
                    pathname: `/events/clone/${event.id}`,
                    state: { event },
                  })
                }
                title={s('clone')}
                id={`clone-${event.id}`}
              />
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                onClick={() => handleModal(event.id)}
                title={s('delete')}
                tone="danger"
                id={`delete-${event.id}`}
              />
            </div>
          </td>
        </tr>
      );
    });
  };

  const EventCard = ({ view, onViewChange, upcomingData, previousData }) => {
    const isPast = view === 'previous';
    const data = isPast ? previousData : upcomingData;
    return (
      <FormCard
        title={
          <>
            <div className="form-card__switch d-none d-md-inline-flex">
              <button
                type="button"
                className={`form-card__switch-btn${
                  view === 'upcoming' ? ' active' : ''
                }`}
                onClick={() => onViewChange('upcoming')}
              >
                {p('futureEvents')}
              </button>
              <button
                type="button"
                className={`form-card__switch-btn${
                  view === 'previous' ? ' active' : ''
                }`}
                onClick={() => onViewChange('previous')}
              >
                {p('pastEvents')}
              </button>
            </div>
            <span className="d-md-none">
              {isPast ? p('pastEvents') : p('futureEvents')}
            </span>
          </>
        }
        actions={
          <>
            {!isPast && (
              <div className="d-none d-md-flex align-items-center">
                <Link to="/events/create">
                  <Button size="sm" color="primary" className="mb-0">
                    {p('createEvent')}
                  </Button>
                </Link>
              </div>
            )}
            <UncontrolledDropdown className="d-md-none">
              <DropdownToggle
                tag="button"
                type="button"
                className="square-icon-btn"
                caret={false}
              >
                <DotsHorizontalIcon />
              </DropdownToggle>
              <DropdownMenu right className="dropdown-menu--modern">
                <DropdownItem
                  active={view === 'upcoming'}
                  onClick={() => onViewChange('upcoming')}
                >
                  {p('futureEvents')}
                </DropdownItem>
                <DropdownItem
                  active={view === 'previous'}
                  onClick={() => onViewChange('previous')}
                >
                  {p('pastEvents')}
                </DropdownItem>
                {!isPast && (
                  <>
                    <DropdownItem divider />
                    <DropdownItem tag={Link} to="/events/create">
                      {p('createEvent')}
                    </DropdownItem>
                  </>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          </>
        }
        bodyClassName="form-card__body--flush"
        className="mb-3"
      >
        <Table responsive striped className="table--roomy">
          <thead>
            <tr>
              <th>{s('title')}</th>
              <th>{s('targetAudience')}</th>
              <th>{p('eventStartDate')}</th>
              <th>
                {p('signedUp')} / {s('status')}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted">
                  {p('noEvents')}
                </td>
              </tr>
            ) : (
              renderEvents(data)
            )}
          </tbody>
        </Table>
      </FormCard>
    );
  };
  return (
    <Container>
      <NavTab
        activeTab={tab}
        tabs={[s('events'), p('eventCategories'), s('Statistics')]}
        setTab={(activeTab) => setTab(activeTab)}
      />
      <DeleteModal
        type="arrangement"
        visible={visible}
        handleModal={handleModal}
        modalAction={deleteEvent}
      />
      <Loading loading={loading} />
      <TabContent activeTab={tab}>
        <TabPane tabId="1">
          <Row>
            <Col>
              <EventCard
                view={eventsView}
                onViewChange={setEventsView}
                upcomingData={events.upcoming}
                previousData={events.previous}
              />
            </Col>
          </Row>
        </TabPane>
        <TabPane tabId="2">
          <EventCategoryCard
            categories={eventCategories}
            loadingCategories={loadingCategories}
            fetchCategories={getEventCategories}
            setLoading={setLoading}
            exportEventCategoriesForAllCompanies={
              exportEventCategoriesForAllCompanies
            }
          />
        </TabPane>
        <LazyTabPane activeTab={tab} tabId="3">
          <ParticipantStatsPage />
        </LazyTabPane>
      </TabContent>
    </Container>
  );
};

export default EventsPage;
